import { useCallback, useEffect, useState } from 'react';

const CURRENT_VERSION = process.env.REACT_APP_VERSION;
const POLL_INTERVAL_MS = 15 * 60 * 1000;
const IS_DEV = process.env.NODE_ENV === 'development';

async function fetchRemoteVersion() {
  const response = await fetch(`/version.json?t=${Date.now()}`, {
    cache: 'no-store',
  });
  if (!response.ok) {
    return null;
  }
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return null;
  }
  const data = await response.json();
  return typeof data?.version === 'string' ? data.version.trim() : null;
}

export default function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const checkVersion = useCallback(async () => {
    if (IS_DEV || !CURRENT_VERSION || updateAvailable) {
      return;
    }
    try {
      const remoteVersion = await fetchRemoteVersion();
      if (remoteVersion && remoteVersion !== CURRENT_VERSION.trim()) {
        setUpdateAvailable(true);
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistration()
            .then((registration) => registration?.update())
            .catch(() => {});
        }
      }
    } catch {
      // Ignore network / parse errors (offline, SPA rewrite, etc.)
    }
  }, [updateAvailable]);

  useEffect(() => {
    checkVersion();

    const intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);

  return updateAvailable;
}
